import { useEffect, useMemo, useRef, useState } from 'react';
import { applySkinTone, SKIN_TONES, type SkinTone } from '@itsappy/shared';
import { useCustomEmoji } from '@/hooks/queries';
import { useKudosEmoji } from '@/hooks/engagement';
import { cn } from '@/lib/utils';
import {
  EMOJI_CATEGORIES,
  TONEABLE,
  recentEmoji,
  rememberEmoji,
  searchEmoji,
  storeSkinTone,
  storedSkinTone,
} from '@/lib/emoji';

/** What a pick hands back: Unicode text, or `:name:` for an org emoji. */
export interface EmojiPick {
  /** What goes in the draft or the reaction. */
  value: string;
  /** Set for custom emoji, so a composer can render the image inline. */
  imageUrl?: string;
}

export interface EmojiPickerProps {
  onPick: (pick: EmojiPick) => void;
  onClose: () => void;
  className?: string;
}

const TONE_SWATCHES: Record<SkinTone, string> = {
  default: '#fcd34d',
  light: '#f5d0b0',
  medium_light: '#e5b183',
  medium: '#c68642',
  medium_dark: '#8d5524',
  dark: '#5a3620',
};

/**
 * The full picker: search, categories, a skin-tone preference that sticks,
 * and the org's own emoji in a tab of their own.
 *
 * The tone applies to the eleven-odd families that accept one and is stored
 * per browser — asking someone to pick their tone on every reaction is the
 * kind of small friction that makes people stop using the feature.
 */
export function EmojiPicker({ onPick, onClose, className }: EmojiPickerProps) {
  const [query, setQuery] = useState('');
  const [tone, setTone] = useState<SkinTone>(storedSkinTone() as SkinTone);
  const [toneOpen, setToneOpen] = useState(false);
  const [recent, setRecent] = useState<string[]>(() => recentEmoji());
  const [tab, setTab] = useState<string>('recent');
  const { data: custom } = useCustomEmoji();
  const kudosEmoji = useKudosEmoji();
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey);
    // Deferred: the click that opened the picker would otherwise close it.
    const timer = window.setTimeout(
      () => window.addEventListener('mousedown', onPointerDown),
      0,
    );
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  // Memoised so the fallback array is not a fresh reference every render,
  // which would re-run the search memo below on every keystroke elsewhere.
  const customEmoji = useMemo(() => custom?.items ?? [], [custom?.items]);
  const searchResults = useMemo(() => searchEmoji(query), [query]);
  const customMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? customEmoji.filter((emoji) => emoji.name.includes(q)) : customEmoji;
  }, [customEmoji, query]);

  function pickUnicode(char: string) {
    const toned = TONEABLE.has(char) ? applySkinTone(char, tone) : char;
    setRecent(rememberEmoji(toned));
    onPick({ value: toned });
  }

  function pickCustom(name: string, url: string) {
    onPick({ value: `:${name}:`, imageUrl: url });
  }

  const tabs = [
    { id: 'recent', label: 'Recent', icon: '🕘' },
    ...EMOJI_CATEGORIES.map((c) => ({
      id: c.id,
      label: c.label,
      icon: c.emoji[0]?.char ?? '•',
    })),
    ...(customEmoji.length ? [{ id: 'custom', label: 'Custom', icon: '⭐' }] : []),
  ];

  const activeCategory = EMOJI_CATEGORIES.find((c) => c.id === tab);
  const searching = query.trim().length > 0;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Choose an emoji"
      className={cn(
        'flex w-[min(21rem,calc(100vw-2rem))] flex-col rounded-xl bg-surface-2 shadow-overlay ring-1 ring-surface-3',
        className,
      )}
    >
      <div className="flex items-center gap-1.5 border-b border-surface-3 p-2">
        <input
          ref={searchRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search emoji"
          aria-label="Search emoji"
          className="min-w-0 flex-1 rounded-lg bg-surface-3 px-2.5 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:ring-1 focus:ring-brand-500"
        />
        <div className="relative">
          <button
            type="button"
            aria-label="Skin tone"
            title="Skin tone"
            aria-expanded={toneOpen}
            onClick={() => setToneOpen((open) => !open)}
            className="grid h-8 w-8 place-items-center rounded-lg hover:bg-surface-3"
          >
            <span
              className="h-4 w-4 rounded-full ring-1 ring-black/20"
              style={{ background: TONE_SWATCHES[tone] }}
            />
          </button>
          {toneOpen && (
            <div className="absolute right-0 top-full z-10 mt-1 flex gap-1 rounded-lg bg-surface-3 p-1 shadow-lg ring-1 ring-surface-4">
              {SKIN_TONES.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-label={`Skin tone: ${option.replace('_', ' ')}`}
                  aria-pressed={option === tone}
                  onClick={() => {
                    setTone(option);
                    storeSkinTone(option);
                    setToneOpen(false);
                  }}
                  className={cn(
                    'grid h-7 w-7 place-items-center rounded-md hover:bg-surface-4',
                    option === tone && 'ring-1 ring-brand-500',
                  )}
                >
                  <span
                    className="h-4 w-4 rounded-full ring-1 ring-black/20"
                    style={{ background: TONE_SWATCHES[option] }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-h-64 min-h-40 overflow-y-auto p-2">
        {/* The workspace's Appy emoji sits above every tab, labelled, so
            nobody finds out it was special by spending one of their five. */}
        {kudosEmoji && !searching && (
          <div className="mb-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setRecent(rememberEmoji(kudosEmoji));
                onPick({ value: kudosEmoji });
              }}
              aria-label={`Give an Appy with ${kudosEmoji}`}
              title="Gives the author an Appy"
              className="ink-brand flex items-center gap-2 rounded-lg border border-brand-500/35 bg-brand-600/10 py-1 pl-1.5 pr-3 transition-colors hover:bg-brand-600/20"
            >
              <span className="grid h-7 w-7 place-items-center text-xl leading-none">{kudosEmoji}</span>
              <span className="text-xs font-semibold">Appy</span>
            </button>
            <span className="text-[11px] text-slate-500">Gives the author an Appy</span>
          </div>
        )}
        {searching ? (
          <>
            {customMatches.length > 0 && (
              <Section title="Custom">
                {customMatches.map((emoji) => (
                  <CustomButton
                    key={emoji.id}
                    name={emoji.name}
                    url={emoji.url}
                    onPick={pickCustom}
                  />
                ))}
              </Section>
            )}
            {searchResults.length > 0 ? (
              <Section title="Emoji">
                {searchResults.map((entry) => (
                  <UnicodeButton
                    key={entry.char}
                    char={TONEABLE.has(entry.char) ? applySkinTone(entry.char, tone) : entry.char}
                    label={entry.keywords[0]}
                    onPick={() => pickUnicode(entry.char)}
                  />
                ))}
              </Section>
            ) : (
              customMatches.length === 0 && (
                <p className="px-1 py-6 text-center text-xs text-slate-500">
                  Nothing matches “{query.trim()}”.
                </p>
              )
            )}
          </>
        ) : tab === 'recent' ? (
          recent.length ? (
            <Section title="Recently used">
              {recent.map((char) => (
                <UnicodeButton key={char} char={char} label={char} onPick={() => pickUnicode(char)} />
              ))}
            </Section>
          ) : (
            <p className="px-1 py-6 text-center text-xs text-slate-500">
              Emoji you use will collect here.
            </p>
          )
        ) : tab === 'custom' ? (
          <Section title="This workspace">
            {customEmoji.map((emoji) => (
              <CustomButton key={emoji.id} name={emoji.name} url={emoji.url} onPick={pickCustom} />
            ))}
          </Section>
        ) : (
          activeCategory && (
            <Section title={activeCategory.label}>
              {activeCategory.emoji.map((entry) => (
                <UnicodeButton
                  key={entry.char}
                  char={TONEABLE.has(entry.char) ? applySkinTone(entry.char, tone) : entry.char}
                  label={entry.keywords[0]}
                  onPick={() => pickUnicode(entry.char)}
                />
              ))}
            </Section>
          )
        )}
      </div>

      <div className="flex gap-0.5 overflow-x-auto border-t border-surface-3 p-1">
        {tabs.map((entry) => (
          <button
            key={entry.id}
            type="button"
            title={entry.label}
            aria-label={entry.label}
            aria-pressed={tab === entry.id && !searching}
            onClick={() => {
              setQuery('');
              setTab(entry.id);
            }}
            className={cn(
              'grid h-8 w-8 shrink-0 place-items-center rounded-md text-base transition-colors',
              tab === entry.id && !searching ? 'bg-surface-3' : 'hover:bg-surface-3',
            )}
          >
            {entry.icon}
          </button>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <p className="px-1 pb-1 text-[11px] uppercase tracking-wide text-slate-500">{title}</p>
      <div className="grid grid-cols-8 gap-0.5">{children}</div>
    </div>
  );
}

function UnicodeButton({
  char,
  label,
  onPick,
}: {
  char: string;
  label: string;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-label={label}
      title={label}
      className="grid h-9 w-9 place-items-center rounded-md text-xl leading-none transition-colors hover:bg-surface-3"
    >
      {char}
    </button>
  );
}

function CustomButton({
  name,
  url,
  onPick,
}: {
  name: string;
  url: string;
  onPick: (name: string, url: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(name, url)}
      aria-label={`:${name}:`}
      title={`:${name}:`}
      className="grid h-9 w-9 place-items-center rounded-md transition-colors hover:bg-surface-3"
    >
      <img src={url} alt={`:${name}:`} className="h-6 w-6 object-contain" loading="lazy" />
    </button>
  );
}
